import { useState } from 'react'
import {
  Check,
  ChevronRight,
  ChevronLeft,
  X,
  Plus,
  Trash2,
  Bell,
  BellOff,
  Pencil,
  ChevronUp,
  ChevronDown,
  TextSelect,
  PenLine,
} from 'lucide-react'
import type {
  WizardStep,
  SampleQuestion,
  SampleGemCriterion,
  NewQuestion,
  NewGemCriterion,
  WizardState,
  ResponseFormat,
  GemCriteriaTemplate,
} from '@/types/streams'
import { SaveAsTemplateModal } from '../gem-criteria'

interface NewStreamWizardProps {
  wizardSteps: WizardStep[]
  sampleQuestions?: SampleQuestion[]
  sampleGemCriteria?: SampleGemCriterion[]
  gemCriteriaTemplates?: GemCriteriaTemplate[]
  onComplete?: (data: WizardState) => void
  onCancel?: () => void
}

export function NewStreamWizard({
  wizardSteps,
  sampleQuestions = [],
  sampleGemCriteria = [],
  gemCriteriaTemplates = [],
  onCancel,
  onComplete,
}: NewStreamWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [questions, setQuestions] = useState<NewQuestion[]>([])
  const [gemCriteria, setGemCriteria] = useState<NewGemCriterion[]>([])

  // UI state
  const [showQuestionForm, setShowQuestionForm] = useState(false)
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null)
  const [newQuestionText, setNewQuestionText] = useState('')
  const [newQuestionDescription, setNewQuestionDescription] = useState('')
  const [newQuestionFormat, setNewQuestionFormat] = useState<ResponseFormat>('single')

  const [showCriteriaForm, setShowCriteriaForm] = useState(false)
  const [editingCriteriaId, setEditingCriteriaId] = useState<string | null>(null)
  const [newCriteriaTitle, setNewCriteriaTitle] = useState('')
  const [newCriteriaDescription, setNewCriteriaDescription] = useState('')
  const [newCriteriaNotify, setNewCriteriaNotify] = useState(false)

  // Template state
  const [showTemplateChoice, setShowTemplateChoice] = useState(false)
  const [templates, setTemplates] = useState<GemCriteriaTemplate[]>(gemCriteriaTemplates)
  const [saveAsTemplateModalOpen, setSaveAsTemplateModalOpen] = useState(false)
  const [criteriaToSaveAsTemplate, setCriteriaToSaveAsTemplate] = useState<NewGemCriterion | null>(null)

  const currentStepData = wizardSteps.find((s) => s.id === currentStep)
  const totalSteps = wizardSteps.length

  const getWizardState = (): WizardState => ({
    currentStep,
    name,
    description,
    sources: [], // Sources are auto-created on the backend
    questions,
    gemCriteria,
  })

  const canProceed = () => {
    if (currentStep === 1) {
      return name.trim().length > 0
    }
    return true
  }

  const handleNext = () => {
    if (!canProceed()) return

    const newCompleted = [...completedSteps]
    if (!newCompleted.includes(currentStep)) {
      newCompleted.push(currentStep)
    }
    setCompletedSteps(newCompleted)

    if (currentStep === totalSteps) {
      onComplete?.(getWizardState())
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkip = () => {
    if (currentStep === totalSteps) {
      onComplete?.(getWizardState())
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  const resetQuestionForm = () => {
    setShowQuestionForm(false)
    setEditingQuestionId(null)
    setNewQuestionText('')
    setNewQuestionDescription('')
    setNewQuestionFormat('single')
  }

  const addQuestion = () => {
    if (!newQuestionText.trim()) return
    const newQ: NewQuestion = {
      id: `q-${Date.now()}`,
      text: newQuestionText,
      description: newQuestionDescription,
      responseFormat: newQuestionFormat,
    }
    setQuestions([...questions, newQ])
    resetQuestionForm()
  }

  const startEditQuestion = (q: NewQuestion) => {
    setEditingQuestionId(q.id)
    setNewQuestionText(q.text)
    setNewQuestionDescription(q.description || '')
    setNewQuestionFormat(q.responseFormat)
    setShowQuestionForm(true)
  }

  const saveEditQuestion = () => {
    if (!newQuestionText.trim() || !editingQuestionId) return
    setQuestions(questions.map((q) =>
      q.id === editingQuestionId
        ? { ...q, text: newQuestionText, description: newQuestionDescription, responseFormat: newQuestionFormat }
        : q
    ))
    resetQuestionForm()
  }

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id))
  }

  const moveQuestion = (id: string, direction: 'up' | 'down') => {
    const index = questions.findIndex((q) => q.id === id)
    if (index === -1) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === questions.length - 1) return

    const newQuestions = [...questions]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newQuestions[index], newQuestions[swapIndex]] = [newQuestions[swapIndex], newQuestions[index]]
    setQuestions(newQuestions)
  }

  const resetCriteriaForm = () => {
    setShowCriteriaForm(false)
    setEditingCriteriaId(null)
    setNewCriteriaTitle('')
    setNewCriteriaDescription('')
    setNewCriteriaNotify(false)
    setShowTemplateChoice(false)
  }

  const handleSelectTemplate = (template: GemCriteriaTemplate) => {
    setNewCriteriaTitle(template.title)
    setNewCriteriaDescription(template.description)
    setNewCriteriaNotify(false)
    setShowCriteriaForm(true)
    setShowTemplateChoice(false)
  }

  const handleCreateFromScratch = () => {
    setShowCriteriaForm(true)
    setShowTemplateChoice(false)
  }

  const openSaveAsTemplateModal = (gc: NewGemCriterion) => {
    setCriteriaToSaveAsTemplate(gc)
    setSaveAsTemplateModalOpen(true)
  }

  const handleTemplateSaved = (template: GemCriteriaTemplate) => {
    setTemplates([...templates, template])
  }

  const addCriteria = () => {
    if (!newCriteriaTitle.trim() || !newCriteriaDescription.trim()) return
    const newGc: NewGemCriterion = {
      id: `gc-${Date.now()}`,
      title: newCriteriaTitle,
      description: newCriteriaDescription,
      notifyImmediately: newCriteriaNotify,
    }
    setGemCriteria([...gemCriteria, newGc])
    resetCriteriaForm()
  }

  const startEditCriteria = (gc: NewGemCriterion) => {
    setEditingCriteriaId(gc.id)
    setNewCriteriaTitle(gc.title)
    setNewCriteriaDescription(gc.description)
    setNewCriteriaNotify(gc.notifyImmediately)
    setShowCriteriaForm(true)
  }

  const saveEditCriteria = () => {
    if (!newCriteriaTitle.trim() || !newCriteriaDescription.trim() || !editingCriteriaId) return
    setGemCriteria(gemCriteria.map((gc) =>
      gc.id === editingCriteriaId
        ? { ...gc, title: newCriteriaTitle, description: newCriteriaDescription, notifyImmediately: newCriteriaNotify }
        : gc
    ))
    resetCriteriaForm()
  }

  const removeCriteria = (id: string) => {
    setGemCriteria(gemCriteria.filter((gc) => gc.id !== id))
  }

  const moveCriteria = (id: string, direction: 'up' | 'down') => {
    const index = gemCriteria.findIndex((gc) => gc.id === id)
    if (index === -1) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === gemCriteria.length - 1) return

    const newCriteria = [...gemCriteria]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newCriteria[index], newCriteria[swapIndex]] = [newCriteria[swapIndex], newCriteria[index]]
    setGemCriteria(newCriteria)
  }

  return (
    <div className="min-h-screen bg-white dark:bg-stone-950">
      {/* Header */}
      <header className="border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={onCancel}
            className="flex items-center gap-1.5 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
          >
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">Cancel</span>
          </button>

          {/* Step indicator */}
          <div className="flex items-center gap-2">
            {wizardSteps.map((step, index) => {
              const isActive = step.id === currentStep
              const isCompleted = completedSteps.includes(step.id) || step.id < currentStep

              return (
                <div key={step.id} className="flex items-center gap-2">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                      isCompleted
                        ? 'bg-teal-600 text-white'
                        : isActive
                          ? 'bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300 ring-2 ring-teal-600 ring-offset-2 dark:ring-offset-stone-950'
                          : 'bg-stone-100 dark:bg-stone-800 text-stone-400 dark:text-stone-500'
                    }`}
                  >
                    {isCompleted ? <Check className="w-3 h-3" /> : step.id}
                  </div>
                  {index < wizardSteps.length - 1 && (
                    <div
                      className={`w-8 h-0.5 ${
                        isCompleted
                          ? 'bg-teal-600'
                          : 'bg-stone-200 dark:bg-stone-700'
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>

          <div className="w-16" /> {/* Spacer for balance */}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-xl mx-auto px-6 py-12 md:py-16">
        {/* Step header */}
        <div className="mb-8 md:mb-10">
          <p className="text-sm text-stone-500 dark:text-stone-400 mb-2">
            Step {currentStep} of {totalSteps}
          </p>
          <h1 className="text-2xl md:text-3xl font-semibold text-stone-900 dark:text-stone-100 mb-3">
            {currentStepData?.title}
          </h1>
          <p className="text-stone-600 dark:text-stone-400 leading-relaxed">
            {currentStepData?.description}
          </p>
        </div>

        {/* Step 1: Name */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                Stream Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Customer Feedback Survey"
                className="w-full px-4 py-3 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                autoFocus
              />
              <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">
                Tip: Use specific names like "Q4 Sales Reports" rather than generic ones.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                Description
                <span className="font-normal text-stone-400 dark:text-stone-500 ml-1">
                  (optional)
                </span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of what this stream collects..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
              />
            </div>
          </div>
        )}

        {/* Step 2: Questions */}
        {currentStep === 2 && (
          <div className="space-y-4">
            {/* Added questions */}
            {questions.map((q, index) => (
              <div key={q.id}>
                {editingQuestionId === q.id ? (
                  /* Inline edit form */
                  <div className="p-5 rounded-lg border-2 border-teal-500 bg-white dark:bg-stone-900 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                        Question
                      </label>
                      <input
                        type="text"
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                        placeholder="What should the AI answer about this data?"
                        className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                        Description
                        <span className="font-normal text-stone-400 dark:text-stone-500 ml-1">
                          (optional)
                        </span>
                      </label>
                      <textarea
                        value={newQuestionDescription}
                        onChange={(e) => setNewQuestionDescription(e.target.value)}
                        placeholder="Additional instructions for AI on how to evaluate this question..."
                        rows={2}
                        className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                      <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                        This helps guide the AI when answering the question.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                        Response Format
                      </label>
                      <div className="space-y-2">
                        <button
                          onClick={() => setNewQuestionFormat('single')}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                            newQuestionFormat === 'single'
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/50'
                              : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'
                          }`}
                        >
                          <p className={`text-sm font-medium ${
                            newQuestionFormat === 'single'
                              ? 'text-teal-700 dark:text-teal-300'
                              : 'text-stone-700 dark:text-stone-300'
                          }`}>
                            Single answer
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                            Best for questions with one clear answer, like "What is the overall sentiment?"
                          </p>
                        </button>
                        <button
                          onClick={() => setNewQuestionFormat('multiple')}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                            newQuestionFormat === 'multiple'
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/50'
                              : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'
                          }`}
                        >
                          <p className={`text-sm font-medium ${
                            newQuestionFormat === 'multiple'
                              ? 'text-teal-700 dark:text-teal-300'
                              : 'text-stone-700 dark:text-stone-300'
                          }`}>
                            Multiple answers
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                            Best for lists or multiple items, like "What feature requests were mentioned?"
                          </p>
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end pt-2">
                      <button
                        onClick={resetQuestionForm}
                        className="px-4 py-2 text-sm text-stone-600 dark:text-stone-400"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveEditQuestion}
                        disabled={!newQuestionText.trim()}
                        className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display card */
                  <div className="p-4 rounded-lg bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="flex-shrink-0 w-6 h-6 rounded bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-stone-900 dark:text-stone-100 text-sm font-medium">{q.text}</p>
                          {q.description && (
                            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1 line-clamp-2">
                              {q.description}
                            </p>
                          )}
                          <p className="text-xs text-stone-400 dark:text-stone-500 mt-1.5">
                            {q.responseFormat === 'single' ? 'Single answer' : 'Multiple answers'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Reorder buttons */}
                        <div className="flex flex-col">
                          <button
                            onClick={() => moveQuestion(q.id, 'up')}
                            disabled={index === 0}
                            className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => moveQuestion(q.id, 'down')}
                            disabled={index === questions.length - 1}
                            className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => startEditQuestion(q)}
                          className="p-1.5 text-stone-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeQuestion(q.id)}
                          className="p-1.5 text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Suggestions when empty */}
            {questions.length === 0 && !showQuestionForm && sampleQuestions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">
                  Suggestions
                </p>
                {sampleQuestions.slice(0, 3).map((sq) => (
                  <button
                    key={sq.id}
                    onClick={() =>
                      setQuestions([...questions, { ...sq, id: `q-${Date.now()}` }])
                    }
                    className="w-full text-left p-3 rounded-lg border border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600 transition-colors"
                  >
                    <p className="text-stone-700 dark:text-stone-300 text-sm">{sq.text}</p>
                    {sq.description && (
                      <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">{sq.description}</p>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Add new question form (only when not editing) */}
            {showQuestionForm && !editingQuestionId ? (
              <div className="p-5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                    Question
                  </label>
                  <input
                    type="text"
                    value={newQuestionText}
                    onChange={(e) => setNewQuestionText(e.target.value)}
                    placeholder="What should the AI answer about this data?"
                    className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                    Description
                    <span className="font-normal text-stone-400 dark:text-stone-500 ml-1">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    value={newQuestionDescription}
                    onChange={(e) => setNewQuestionDescription(e.target.value)}
                    placeholder="Additional instructions for AI on how to evaluate this question..."
                    rows={2}
                    className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  />
                  <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                    This helps guide the AI when answering the question.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                    Response Format
                  </label>
                  <div className="space-y-2">
                    <button
                      onClick={() => setNewQuestionFormat('single')}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        newQuestionFormat === 'single'
                          ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/50'
                          : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'
                      }`}
                    >
                      <p className={`text-sm font-medium ${
                        newQuestionFormat === 'single'
                          ? 'text-teal-700 dark:text-teal-300'
                          : 'text-stone-700 dark:text-stone-300'
                      }`}>
                        Single answer
                      </p>
                      <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                        Best for questions with one clear answer, like "What is the overall sentiment?"
                      </p>
                    </button>
                    <button
                      onClick={() => setNewQuestionFormat('multiple')}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        newQuestionFormat === 'multiple'
                          ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/50'
                          : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'
                      }`}
                    >
                      <p className={`text-sm font-medium ${
                        newQuestionFormat === 'multiple'
                          ? 'text-teal-700 dark:text-teal-300'
                          : 'text-stone-700 dark:text-stone-300'
                      }`}>
                        Multiple answers
                      </p>
                      <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                        Best for lists or multiple items, like "What feature requests were mentioned?"
                      </p>
                    </button>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    onClick={resetQuestionForm}
                    className="px-4 py-2 text-sm text-stone-600 dark:text-stone-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addQuestion}
                    disabled={!newQuestionText.trim()}
                    className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : !editingQuestionId && (
              <button
                onClick={() => setShowQuestionForm(true)}
                className="w-full flex items-center justify-center gap-2 p-4 rounded-lg border border-dashed border-stone-300 dark:border-stone-600 text-stone-500 dark:text-stone-400 hover:border-stone-400 hover:text-stone-600 dark:hover:border-stone-500 dark:hover:text-stone-300 transition-all text-sm"
              >
                <Plus className="w-4 h-4" />
                Add a question
              </button>
            )}

            {questions.length === 0 && !showQuestionForm && (
              <p className="text-sm text-stone-500 dark:text-stone-400 text-center pt-2">
                Be specific — "What are the top 3 feature requests?" works better than "What do people want?"
              </p>
            )}
          </div>
        )}

        {/* Step 3: Gem Criteria */}
        {currentStep === 3 && (
          <div className="space-y-4">
            {/* Added criteria */}
            {gemCriteria.map((gc, index) => (
              <div key={gc.id}>
                {editingCriteriaId === gc.id ? (
                  /* Inline edit form */
                  <div className="p-5 rounded-lg border-2 border-teal-500 bg-white dark:bg-stone-900 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={newCriteriaTitle}
                        onChange={(e) => setNewCriteriaTitle(e.target.value)}
                        placeholder="e.g., Competitor Mention"
                        className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        autoFocus
                      />
                      <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                        A short name for this criteria (used in notifications).
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                        Description
                      </label>
                      <textarea
                        value={newCriteriaDescription}
                        onChange={(e) => setNewCriteriaDescription(e.target.value)}
                        placeholder="e.g., Any mention of a competitor"
                        rows={2}
                        className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                      <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                        Describe what the AI should look for to flag an item as a gem.
                      </p>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newCriteriaNotify}
                        onChange={(e) => setNewCriteriaNotify(e.target.checked)}
                        className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm text-stone-700 dark:text-stone-300">
                        Notify me immediately when found
                      </span>
                    </label>

                    <div className="flex gap-2 justify-end pt-2">
                      <button
                        onClick={resetCriteriaForm}
                        className="px-4 py-2 text-sm text-stone-600 dark:text-stone-400"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveEditCriteria}
                        disabled={!newCriteriaTitle.trim() || !newCriteriaDescription.trim()}
                        className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display card */
                  <div className="p-4 rounded-lg bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            gc.notifyImmediately
                              ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                              : 'bg-stone-200 dark:bg-stone-700 text-stone-500 dark:text-stone-400'
                          }`}
                        >
                          {gc.notifyImmediately ? (
                            <Bell className="w-3.5 h-3.5" />
                          ) : (
                            <BellOff className="w-3.5 h-3.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-stone-900 dark:text-stone-100 text-sm font-medium">
                            {gc.title}
                          </p>
                          <p className="text-stone-600 dark:text-stone-400 text-sm mt-0.5">
                            {gc.description}
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                            {gc.notifyImmediately ? 'Notify immediately' : 'Silent'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Reorder buttons */}
                        <div className="flex flex-col">
                          <button
                            onClick={() => moveCriteria(gc.id, 'up')}
                            disabled={index === 0}
                            className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => moveCriteria(gc.id, 'down')}
                            disabled={index === gemCriteria.length - 1}
                            className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => openSaveAsTemplateModal(gc)}
                          className="p-1.5 text-stone-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
                          title="Save as template"
                        >
                          <TextSelect className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEditCriteria(gc)}
                          className="p-1.5 text-stone-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeCriteria(gc.id)}
                          className="p-1.5 text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Suggestions when empty */}
            {gemCriteria.length === 0 && !showCriteriaForm && sampleGemCriteria.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-stone-500 dark:text-stone-400 uppercase tracking-wide">
                  Suggestions
                </p>
                {sampleGemCriteria.slice(0, 3).map((sgc) => (
                  <button
                    key={sgc.id}
                    onClick={() =>
                      setGemCriteria([...gemCriteria, { ...sgc, id: `gc-${Date.now()}` }])
                    }
                    className="w-full text-left p-3 rounded-lg border border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600 transition-colors"
                  >
                    <p className="text-stone-900 dark:text-stone-100 text-sm font-medium">{sgc.title}</p>
                    <p className="text-stone-500 dark:text-stone-400 text-sm mt-0.5">{sgc.description}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Add new criteria form (only when not editing) */}
            {showCriteriaForm && !editingCriteriaId ? (
              <div className="p-5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={newCriteriaTitle}
                    onChange={(e) => setNewCriteriaTitle(e.target.value)}
                    placeholder="e.g., Competitor Mention"
                    className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    autoFocus
                  />
                  <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                    A short name for this criteria (used in notifications).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newCriteriaDescription}
                    onChange={(e) => setNewCriteriaDescription(e.target.value)}
                    placeholder="e.g., Any mention of a competitor"
                    rows={2}
                    className="w-full px-3 py-2.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  />
                  <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                    Describe what the AI should look for to flag an item as a gem.
                  </p>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newCriteriaNotify}
                    onChange={(e) => setNewCriteriaNotify(e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-stone-700 dark:text-stone-300">
                    Notify me immediately when found
                  </span>
                </label>

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    onClick={resetCriteriaForm}
                    className="px-4 py-2 text-sm text-stone-600 dark:text-stone-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addCriteria}
                    disabled={!newCriteriaTitle.trim() || !newCriteriaDescription.trim()}
                    className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : !editingCriteriaId && (
              <>
                {showTemplateChoice && templates.length > 0 ? (
                  <div className="p-5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 space-y-4">
                    <p className="text-sm text-stone-600 dark:text-stone-400">
                      How would you like to add a gem criteria?
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={handleCreateFromScratch}
                        className="flex flex-col items-center gap-2 p-4 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 hover:border-stone-300 dark:hover:border-stone-600 transition-colors"
                      >
                        <PenLine className="w-5 h-5" />
                        <span className="text-sm font-medium">Create from scratch</span>
                      </button>
                      <div className="relative">
                        <select
                          onChange={(e) => {
                            const template = templates.find((t) => t.id === e.target.value)
                            if (template) handleSelectTemplate(template)
                          }}
                          defaultValue=""
                          className="w-full h-full opacity-0 absolute inset-0 cursor-pointer"
                        >
                          <option value="" disabled>Select template</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.title}
                            </option>
                          ))}
                        </select>
                        <div className="flex flex-col items-center gap-2 p-4 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 hover:border-stone-300 dark:hover:border-stone-600 transition-colors pointer-events-none">
                          <TextSelect className="w-5 h-5" />
                          <span className="text-sm font-medium">Use a template</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowTemplateChoice(false)}
                      className="w-full text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => templates.length > 0 ? setShowTemplateChoice(true) : setShowCriteriaForm(true)}
                    className="w-full flex items-center justify-center gap-2 p-4 rounded-lg border border-dashed border-stone-300 dark:border-stone-600 text-stone-500 dark:text-stone-400 hover:border-stone-400 hover:text-stone-600 dark:hover:border-stone-500 dark:hover:text-stone-300 transition-all text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add gem criteria
                  </button>
                )}
              </>
            )}

            {gemCriteria.length === 0 && !showCriteriaForm && (
              <p className="text-sm text-stone-500 dark:text-stone-400 text-center pt-2">
                Think about what would make you stop and take action on a single item.
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12 pt-6 border-t border-stone-200 dark:border-stone-800">
          <div>
            {currentStep > 1 ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div />
            )}
          </div>

          <div className="flex items-center gap-4">
            {!currentStepData?.required && (
              <button
                onClick={handleSkip}
                className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
              >
                Skip for now
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                canProceed()
                  ? 'bg-teal-600 hover:bg-teal-700 text-white'
                  : 'bg-stone-100 dark:bg-stone-800 text-stone-400 dark:text-stone-500 cursor-not-allowed'
              }`}
            >
              {currentStep === totalSteps ? 'Create Stream' : 'Continue'}
              {currentStep < totalSteps && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </main>

      {/* Save as template modal */}
      {criteriaToSaveAsTemplate && (
        <SaveAsTemplateModal
          isOpen={saveAsTemplateModalOpen}
          onClose={() => {
            setSaveAsTemplateModalOpen(false)
            setCriteriaToSaveAsTemplate(null)
          }}
          gemCriterion={{
            title: criteriaToSaveAsTemplate.title,
            description: criteriaToSaveAsTemplate.description,
          }}
          templates={templates}
          onSaved={handleTemplateSaved}
        />
      )}
    </div>
  )
}
